import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonText,
  IonSpinner,
  IonToast,
  IonGrid,
  IonRow,
  IonCol,
  IonCheckbox,
  IonItem,
  IonLabel,
  IonList,
  IonProgressBar,
} from '@ionic/react';
import {
  cloudDownloadOutline,
  checkmarkCircleOutline,
  documentTextOutline,
  calendarOutline,
  cartOutline,
  ticketOutline,
  businessOutline,
  linkOutline,
  statsChartOutline,
  downloadOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  eventService,
  saleService,
  ticketService,
  supplierService,
  fulfillmentService,
} from '../services/api';
import { Event, Sale, Ticket, Supplier } from '../types';
import { exportAllDataToExcel } from '../utils/excelUtils';
import { format } from 'date-fns';

interface ExportOptions {
  includeEvents: boolean;
  includeSales: boolean;
  includeTickets: boolean;
  includeSuppliers: boolean;
  includeFulfillments: boolean;
}

const Export: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeEvents: true,
    includeSales: true,
    includeTickets: true,
    includeSuppliers: true,
    includeFulfillments: false, // Default to false as it's less commonly needed
  });

  const [dataCounts, setDataCounts] = useState({
    events: 0,
    sales: 0,
    tickets: 0,
    suppliers: 0,
    fulfillments: 0,
  });

  useEffect(() => {
    loadDataCounts();
  }, []);

  const loadDataCounts = async () => {
    try {
      setLoading(true);
      
      const [events, sales, tickets, suppliers, fulfillments] = await Promise.all([
        eventService.getAllEvents(),
        saleService.getAll(),
        ticketService.getAll(),
        supplierService.getAllWithBalance(),
        fulfillmentService.getAll().catch(() => []), // Gracefully handle if endpoint doesn't exist yet
      ]);

      setDataCounts({
        events: events?.length || 0,
        sales: sales?.length || 0,
        tickets: tickets?.length || 0,
        suppliers: suppliers?.length || 0,
        fulfillments: fulfillments?.length || 0,
      });
    } catch (error) {
      console.error('Error loading data counts:', error);
      setToastMessage('Failed to load data counts');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      setProgress(0);

      const exportData: any = {};
      let totalSteps = 0;
      let currentStep = 0;

      // Count total steps
      if (exportOptions.includeEvents) totalSteps++;
      if (exportOptions.includeSales) totalSteps++;
      if (exportOptions.includeTickets) totalSteps++;
      if (exportOptions.includeSuppliers) totalSteps++;
      if (exportOptions.includeFulfillments) totalSteps++;

      // Load Events
      if (exportOptions.includeEvents) {
        setProgress((currentStep / totalSteps) * 100);
        try {
          exportData.events = await eventService.getAllEvents();
          currentStep++;
        } catch (error) {
          console.error('Error loading events:', error);
        }
      }

      // Load Sales
      if (exportOptions.includeSales) {
        setProgress((currentStep / totalSteps) * 100);
        try {
          exportData.sales = await saleService.getAll();
          currentStep++;
        } catch (error) {
          console.error('Error loading sales:', error);
        }
      }

      // Load Tickets
      if (exportOptions.includeTickets) {
        setProgress((currentStep / totalSteps) * 100);
        try {
          exportData.tickets = await ticketService.getAll();
          currentStep++;
        } catch (error) {
          console.error('Error loading tickets:', error);
        }
      }

      // Load Suppliers
      if (exportOptions.includeSuppliers) {
        setProgress((currentStep / totalSteps) * 100);
        try {
          exportData.suppliers = await supplierService.getAllWithBalance();
          currentStep++;
        } catch (error) {
          console.error('Error loading suppliers:', error);
        }
      }

      // Load Fulfillments
      if (exportOptions.includeFulfillments) {
        setProgress((currentStep / totalSteps) * 100);
        try {
          exportData.fulfillments = await fulfillmentService.getAll();
          currentStep++;
        } catch (error) {
          console.error('Error loading fulfillments:', error);
          exportData.fulfillments = []; // Fallback to empty array
        }
      }

      setProgress(100);

      // Generate filename with timestamp
      const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
      const filename = `ticket_system_export_${timestamp}.xlsx`;

      // Export to Excel
      exportAllDataToExcel(exportData, {
        ...exportOptions,
        filename,
      });

      setToastMessage('Export completed successfully!');
      setShowToast(true);
    } catch (error: any) {
      console.error('Export error:', error);
      setToastMessage(error.message || 'Failed to export data');
      setShowToast(true);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const handleOptionChange = (option: keyof ExportOptions) => {
    setExportOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const exportItems = [
    {
      key: 'includeEvents' as keyof ExportOptions,
      title: 'Events',
      description: 'All event information including dates, venues, and status',
      icon: calendarOutline,
      count: dataCounts.events,
      color: 'primary'
    },
    {
      key: 'includeSales' as keyof ExportOptions,
      title: 'Sales',
      description: 'Sales records with buyer information and transaction details',
      icon: cartOutline,
      count: dataCounts.sales,
      color: 'success'
    },
    {
      key: 'includeTickets' as keyof ExportOptions,
      title: 'Inventory',
      description: 'Ticket inventory with pricing and availability information',
      icon: ticketOutline,
      count: dataCounts.tickets,
      color: 'warning'
    },
    {
      key: 'includeSuppliers' as keyof ExportOptions,
      title: 'Suppliers',
      description: 'Supplier contacts and payment information',
      icon: businessOutline,
      count: dataCounts.suppliers,
      color: 'secondary'
    },
    {
      key: 'includeFulfillments' as keyof ExportOptions,
      title: 'Fulfillments',
      description: 'Links between sales and inventory items',
      icon: linkOutline,
      count: dataCounts.fulfillments,
      color: 'tertiary'
    }
  ];

  const selectedCount = Object.values(exportOptions).filter(Boolean).length;
  const totalRecords = Object.entries(exportOptions)
    .filter(([key, enabled]) => enabled)
    .reduce((sum, [key]) => {
      let dataKey = key.replace('include', '').toLowerCase();
      // Handle plural forms correctly
      if (dataKey === 'supplier') dataKey = 'suppliers';
      else if (dataKey === 'fulfillment') dataKey = 'fulfillments';
      else dataKey = dataKey + 's';
      
      return sum + (dataCounts[dataKey as keyof typeof dataCounts] || 0);
    }, 0);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton menu="main-menu" />
          </IonButtons>
          <IonTitle>Export Data</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">Export Data</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="p-4">
          {/* Header Card */}
          <IonCard className="mb-4">
            <IonCardHeader>
              <IonCardTitle className="flex items-center">
                <IonIcon icon={cloudDownloadOutline} className="mr-2 text-primary" />
                Export Your Data
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText color="medium">
                <p className="text-sm mb-4">
                  Export your ticket system data to Excel format with multiple tabs. 
                  Select the data types you want to include in your export.
                </p>
              </IonText>

              {/* Summary */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-blue-900 dark:text-blue-100">
                    Export Summary
                  </span>
                  <IonIcon icon={statsChartOutline} className="text-blue-600" />
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">Selected Types:</span>
                    <span className="ml-2 font-medium">{selectedCount}</span>
                  </div>
                  <div>
                    <span className="text-blue-700 dark:text-blue-300">Total Records:</span>
                    <span className="ml-2 font-medium">{totalRecords.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Data Selection */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Select Data to Export</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList>
                {exportItems.map((item) => (
                  <IonItem key={item.key} button onClick={() => handleOptionChange(item.key)}>
                    <IonIcon
                      icon={item.icon}
                      slot="start"
                      className={`text-${item.color}`}
                    />
                    <IonLabel>
                      <h3 className="font-medium">{item.title}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {item.description}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.count.toLocaleString()} records
                      </p>
                    </IonLabel>
                    <IonCheckbox
                      slot="end"
                      checked={exportOptions[item.key]}
                      onIonChange={() => handleOptionChange(item.key)}
                    />
                  </IonItem>
                ))}
              </IonList>
            </IonCardContent>
          </IonCard>

          {/* Export Actions */}
          <IonCard className="mt-4">
            <IonCardContent>
              {loading && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Exporting data...</span>
                    <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
                  </div>
                  <IonProgressBar value={progress / 100} color="primary" />
                </div>
              )}

              <div className="flex gap-3">
                <IonButton
                  expand="block"
                  onClick={handleExport}
                  disabled={loading || selectedCount === 0}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <IonSpinner name="crescent" className="mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <IonIcon icon={downloadOutline} slot="start" />
                      Export to Excel
                    </>
                  )}
                </IonButton>

                <IonButton
                  fill="outline"
                  onClick={loadDataCounts}
                  disabled={loading}
                >
                  <IonIcon icon={checkmarkCircleOutline} slot="icon-only" />
                </IonButton>
              </div>

              {selectedCount === 0 && (
                <IonText color="warning">
                  <p className="text-sm mt-2 text-center">
                    Please select at least one data type to export.
                  </p>
                </IonText>
              )}
            </IonCardContent>
          </IonCard>

          {/* Information */}
          <IonCard className="mt-4">
            <IonCardContent>
              <div className="space-y-3">
                <div className="flex items-start">
                  <IonIcon icon={documentTextOutline} className="text-gray-400 mr-3 mt-1" />
                  <div>
                    <h4 className="font-medium text-sm">Excel Format</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Data will be exported as an Excel file (.xlsx) with separate tabs for each data type.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <IonIcon icon={statsChartOutline} className="text-gray-400 mr-3 mt-1" />
                  <div>
                    <h4 className="font-medium text-sm">Summary Tab</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Includes overview statistics and export information.
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <IonIcon icon={checkmarkCircleOutline} className="text-gray-400 mr-3 mt-1" />
                  <div>
                    <h4 className="font-medium text-sm">Data Integrity</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      All exported data reflects the current state of your system.
                    </p>
                  </div>
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        </div>

        {/* Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="bottom"
        />
      </IonContent>
    </IonPage>
  );
};

export default Export;